import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { ScoreCard, MiniScore } from "../components/shared/ScoreCard";
import { NextActionCard } from "../components/shared/NextActionCard";
import { EmptyState } from "../components/shared/EmptyState";
import { Collapse } from "../components/shared/Collapse";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Compass, ArrowRight, FolderKanban, Send, TrendingUp } from "lucide-react";
import { VoiceButton } from "../components/shared/VoiceButton";
import { capture, EVENTS } from "../analytics/posthog";

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [goal, setGoal] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, []);

  const createProject = async () => {
    if (!goal.trim()) return;
    setCreating(true);
    try {
      const { data: p } = await api.post("/projects", { objective: goal.trim() });
      capture(EVENTS.projectCreated, { source: "dashboard" });
      navigate(`/app/projects/${p.id}`);
    } catch (e) { toast.error(apiErr(e)); setCreating(false); }
  };

  if (loading) return <div className="mono-label text-slate-500">CARGANDO…</div>;

  if (!data?.has_audit) {
    return (
      <EmptyState icon={Compass} title="Aún no has hecho tu diagnóstico"
        description="Completa el Business Independence Audit para descubrir dónde tu empresa depende de ti y obtener tu Independence Score."
        testId="dashboard-empty-audit"
        action={<Button data-testid="dashboard-start-audit" onClick={() => navigate("/audit")} className="bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold">Comenzar diagnóstico <ArrowRight className="ml-2 h-4 w-4" /></Button>} />
    );
  }

  const s = data.scores;
  const rc = data.recent_change;
  const rcDiff = rc ? (rc.independence_after ?? 0) - (rc.independence_before ?? 0) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold text-white">Hola, {data.workspace?.name}</h1>
        <p className="mt-1 text-sm text-slate-400">{data.workspace?.sector} · Panel de independencia</p>
      </div>

      {/* FIRST LEVEL: Score + recent change + single next best action */}
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="space-y-4 min-w-0">
          <ScoreCard testId="dashboard-independence-score" score={s.independence_score} title="Índice de Independencia" category={s.independence_category} size={150} />
          {rc && rcDiff > 0 && (
            <div className="card-arch p-4 flex items-center justify-between gap-3" data-testid="dashboard-recent-change">
              <div className="flex items-center gap-2 text-emerald-400">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm font-medium">Cambio reciente</span>
              </div>
              <span className="font-mono text-sm text-slate-300">{rc.independence_before} → <span className="text-emerald-400 font-semibold">{rc.independence_after}</span> <span className="text-emerald-400">(+{rcDiff})</span></span>
            </div>
          )}
        </div>
        <div className="lg:pt-8 min-w-0">
          <NextActionCard action={data.next_action} />
        </div>
      </div>

      {/* SECOND LEVEL: create project input */}
      <div className="card-arch p-5">
        <div className="halo" />
        <label className="relative mono-label text-cyan-400">¿Qué otro objetivo quieres conseguir?</label>
        <div className="relative mt-3 flex flex-col sm:flex-row sm:items-end gap-2">
          <input data-testid="dashboard-goal-input" value={goal} onChange={(e) => setGoal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createProject()}
            placeholder="Ej. Poder ausentarme 30 días · Reducir dependencia de María…"
            className="flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/40" />
          <div className="flex gap-2">
            <VoiceButton testId="dashboard-voice-button" onTranscript={(t) => setGoal((p) => (p ? p + " " : "") + t)} />
            <Button data-testid="dashboard-create-project" onClick={createProject} disabled={creating || !goal.trim()}
              className="h-11 flex-1 sm:flex-none shrink-0 rounded-xl bg-amber-500 text-black hover:bg-amber-400 font-semibold px-4">
              {creating ? "Creando…" : <>Crear proyecto <Send className="ml-2 h-4 w-4" /></>}
            </Button>
          </div>
        </div>
      </div>

      {/* Active projects */}
      <div className="card-arch p-5 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-cyan-400"><FolderKanban className="h-4 w-4" /><span className="mono-label">Proyectos activos</span></div>
          <button onClick={() => navigate("/app/projects")} className="text-xs text-slate-400 hover:text-white">Ver todos</button>
        </div>
        <div className="mt-4 space-y-3">
          {data.active_projects.length === 0 && <p className="text-sm text-slate-500">Sin proyectos activos. Crea uno arriba.</p>}
          {data.active_projects.map((p) => (
            <button key={p.id} data-testid={`dashboard-project-${p.id}`} onClick={() => navigate(`/app/projects/${p.id}`)}
              className="w-full text-left rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:border-amber-500/30 transition-colors">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-white truncate">{p.objective}</span>
                <span className="mono-label text-slate-500 shrink-0">{p.progress}%</span>
              </div>
              {p.stage_summary && (
                <p className="mt-1 text-xs text-slate-500">{p.stage_summary.verified}/{p.stage_summary.systems_total} verificados · {p.stage_summary.implemented} en marcha</p>
              )}
              <div className="mt-2 h-1 rounded-full bg-white/5"><div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${p.progress}%` }} /></div>
            </button>
          ))}
        </div>
      </div>

      {/* THIRD LEVEL: secondary metrics, tucked away */}
      <Collapse title="Más métricas de independencia" testId="dashboard-more-metrics">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-2">
          <MiniScore testId="dashboard-owner-dependency" label="Dependencia del propietario" value={s.owner_dependency_score} tone="red" />
          <MiniScore testId="dashboard-key-person" label="Riesgo por persona clave" value={s.key_person_risk} tone="amber" />
          <MiniScore testId="dashboard-interventions" label="Intervenciones" value={data.interventions_count} tone="cyan" />
        </div>
      </Collapse>
    </div>
  );
}
